import React from 'react';
import {
  FormControlLabel,
  Checkbox,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Slider,
  Button,
  Divider,
  Box,
  CircularProgress,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import useStore from '../../../../store/store';
import usePersistentStore from '../../../../store/persistentStore';
import usePersistentAuthStore from '../../../../store/persistentAuthStore';
import {ComparisonModeRadio} from '../../ComparisonModeSelect';
import {
  graphSettings,
  STACKED_REPORT_GRAPH_HEIGHT,
  REALTIME_REFRESH_INTERVAL_OPTIONS,
  getRealtimeRefreshIntervalSecondsOrDefault,
} from '../helpers/configs';

const useGraphHeightSliderStyles = makeStyles(theme => ({
  /**
   * MUI places marks with `left: N%` on the mark's left edge; the thumb uses negative margin to
   * center on that point. Match that so the default tick lines up with the thumb at the same value.
   */
  mark: {
    width: 6,
    height: 22,
    borderRadius: 3,
    backgroundColor: theme.palette.primary.main,
    border: `1px solid ${theme.palette.background.paper}`,
    boxSizing: 'border-box',
    opacity: 1,
    top: 3,
    transform: 'translateX(-50%)',
  },
}));

const GraphControls = React.memo(
  ({
    type,
    period,
    resellerCommission = null,
    panelChangesLoading = false,
    /** 'row' = toolbar (wrap); 'column' = stacked for popover / menu */
    layout = 'row',
  }) => {
    const graphHeightSliderClasses = useGraphHeightSliderStyles();
    const {auth} = usePersistentAuthStore();
    const stickyGraphEnabled = usePersistentStore(state => state.stickyGraphEnabled);
    const showNetPrebidResellerRevenue = usePersistentStore(
      state => state.showNetPrebidResellerRevenue
    );
    const analyticsChangeLogEnabled = usePersistentStore(
      state => state.analyticsChangeLogEnabled ?? true
    );
    const prebidSspChangesEnabled = usePersistentStore(
      state => state.prebidSspChangesEnabled ?? true
    );
    const groupingEnabled = useStore(state => state.groupingEnabled);
    const prebidSplitMode = useStore(state => state.prebidSplitMode);
    const setState = useStore(state => state.setState);
    const fixedScale = usePersistentStore(state => state.fixedScale);
    const centerTooltip = usePersistentStore(state => state.centerTooltip);
    const disableMillify = usePersistentStore(state => state.disableMillify);
    const autoRefreshEnabled = usePersistentStore(state => state.autoRefreshEnabled);
    const maxVisibleColumns = usePersistentStore(state => state.maxVisibleColumns);
    const analyticsGrouping = usePersistentStore(state => state.analyticsGrouping) ?? 'bidder';
    const comparisonEnabled = usePersistentStore(state => state.comparisonEnabled);
    const persistedRealtimeMinutes = usePersistentStore(state => state.realtimeMinutes);
    const setPersistentState = usePersistentStore(state => state.setState);
    const stackedReportGraphHeightPx = usePersistentStore(
      state => state.stackedReportGraphHeightPx
    );
    const stackedReportGraphHidden = Boolean(
      usePersistentStore(state => state.stackedReportGraphHidden)
    );
    const graphHeightSliderMin = STACKED_REPORT_GRAPH_HEIGHT.min;
    const graphHeightSliderMax = STACKED_REPORT_GRAPH_HEIGHT.max;
    const rawGraphHeight = stackedReportGraphHeightPx ?? graphSettings.wrapperHeight;
    const graphHeightSliderValue = Math.min(
      graphHeightSliderMax,
      Math.max(graphHeightSliderMin, rawGraphHeight)
    );
    const isGraphHeightAtDefault = graphHeightSliderValue === graphSettings.wrapperHeight;
    const realtimeMinutes = persistedRealtimeMinutes ?? 10;
    const realtimeMinuteOptions = [5, 10, 20, 40, 60];
    const rawRealtimeRefreshInterval = usePersistentStore(
      state => state.realtimeRefreshIntervalSeconds
    );
    const realtimeRefreshIntervalSeconds = getRealtimeRefreshIntervalSecondsOrDefault(
      rawRealtimeRefreshInterval
    );

    const isGroupingEnabled =
      type === 'prebid' || (type === 'analytics' && analyticsGrouping === 'bidder');

    // Disable comparison when:
    // - period is "All history" for any stacked report type, OR
    // - analytics is in "realtime", "Last 24 hours", or "Today"
    const isComparisonDisabled =
      period?.label === 'All history' ||
      (type === 'analytics' &&
        (period?.label === 'realtime' ||
          period?.label === 'Last 24 hours' ||
          period?.label === 'Today'));

    const showNetPrebidResellerRevenueOption =
      (type === 'prebid' || type === 'demand') &&
      auth?.type === 7 &&
      resellerCommission != null &&
      resellerCommission !== '';

    let controlsConfig = [
      {
        title: 'Server/Client split',
        tooltip: 'Group demand by the Prebid server and Prebid client side.',
        checked: !groupingEnabled && prebidSplitMode === 'serverClient',
        onChange: () => {
          const isChecked = !groupingEnabled && prebidSplitMode === 'serverClient';
          if (isChecked) {
            setState('groupingEnabled', true);
          } else {
            setState('prebidSplitMode', 'serverClient');
            setState('groupingEnabled', false);
          }
        },
        hidden: type !== 'analytics' || analyticsGrouping !== 'bidder',
      },
      {
        title: 'Auto refresh',
        tooltip:
          'Fetch the latest data automatically when enabled. The schedule is set under Auto refresh interval.',
        checked: autoRefreshEnabled,
        onChange: () => setPersistentState('autoRefreshEnabled', !autoRefreshEnabled),
        hidden: period?.label !== 'realtime',
      },
      {
        title: 'Sticky graph',
        tooltip: 'Keep the graph visible while scrolling table below.',
        checked: stickyGraphEnabled,
        onChange: () => setPersistentState('stickyGraphEnabled', !stickyGraphEnabled),
        disabled: stackedReportGraphHidden,
      },
      {
        title: 'Hide graph',
        tooltip: 'Hide the chart and show only the data table.',
        checked: stackedReportGraphHidden,
        onChange: () => setPersistentState('stackedReportGraphHidden', !stackedReportGraphHidden),
      },
      {
        title: 'Centered graph tooltip',
        tooltip: 'Center the tooltip to scroll easier.',
        checked: centerTooltip,
        onChange: () => setPersistentState('centerTooltip', !centerTooltip),
      },
      {
        title: 'Lock scale for comparison',
        tooltip:
          'Locks the vertical axis in the graph. This keeps the visual proportions exactly the same when you toggle different metrics on or off.',
        checked: fixedScale,
        onChange: () => setPersistentState('fixedScale', !fixedScale),
        disabled: !groupingEnabled,
      },
      {
        title: 'Show full numbers',
        tooltip: 'Show full values instead of millified ones (e.g. 1,000,000 instead of 1M).',
        checked: disableMillify,
        onChange: () => setPersistentState('disableMillify', !disableMillify),
        analyticsExcluded: true, // Hide this option for analytics reports
      },
      {
        key: 'changeLog',
        title: 'Show change history',
        tooltip:
          'Show configuration changes on the timeline. Click a marker to open that day’s change log.',
        checked: analyticsChangeLogEnabled,
        onChange: () => setPersistentState('analyticsChangeLogEnabled', !analyticsChangeLogEnabled),
        analyticsOnly: true,
      },
      {
        key: 'sspChanges',
        title: 'Show SSP changes',
        tooltip:
          'Show Direct/Reseller SSP switches on the timeline. Click a marker to view details.',
        checked: prebidSspChangesEnabled,
        onChange: () => setPersistentState('prebidSspChangesEnabled', !prebidSspChangesEnabled),
        prebidOnly: true,
      },
      {
        key: 'comparison',
        title: 'Show data comparison ',
        tooltip: isComparisonDisabled ? 'Comparison is not available for this period.' : '',
        checked: comparisonEnabled,
        onChange: () => setPersistentState('comparisonEnabled', !comparisonEnabled),
        showComparisonSpinner: true,
        disabled: isComparisonDisabled,
      },
      {
        title: 'Net reseller revenue',
        tooltip: `Show reseller revenue after your ${resellerCommission}% commission is deducted.`,
        checked: showNetPrebidResellerRevenue,
        onChange: () =>
          setPersistentState('showNetPrebidResellerRevenue', !showNetPrebidResellerRevenue),
        hidden: !showNetPrebidResellerRevenueOption,
      },
    ];

    const handleVisibleColumnsChange = event => {
      const value = Number(event.target.value);
      setPersistentState('maxVisibleColumns', value);
      // Reset carousel position to start to avoid empty selections
      setPersistentState('columnsStartIndex', 0);
    };

    const isColumn = layout === 'column';

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: isColumn ? 'column' : 'row',
          flexWrap: isColumn ? 'nowrap' : 'wrap',
          alignItems: isColumn ? 'stretch' : 'center',
          gap: isColumn ? 12 : 8,
        }}
      >
        {type === 'analytics' &&
          period?.label === 'realtime' &&
          (isColumn ? (
            <div>
              <Typography variant="body2" component="div" style={{marginBottom: 8}}>
                Realtime data window
              </Typography>
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  value={realtimeMinutes}
                  onChange={event => {
                    const nextValue = Number(event.target.value);
                    setPersistentState('realtimeMinutes', nextValue);
                  }}
                  aria-label="Realtime data window"
                >
                  {realtimeMinuteOptions.map(value => (
                    <MenuItem key={value} value={value}>
                      {value} minutes
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          ) : (
            <FormControl variant="outlined" size="small" style={{minWidth: 150}}>
              <InputLabel id="realtime-minutes-label">Realtime data window</InputLabel>
              <Select
                labelId="realtime-minutes-label"
                value={realtimeMinutes}
                label="Realtime data window"
                onChange={event => {
                  const nextValue = Number(event.target.value);
                  setPersistentState('realtimeMinutes', nextValue);
                }}
              >
                {realtimeMinuteOptions.map(value => (
                  <MenuItem key={value} value={value}>
                    {value} minutes
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}

        {period?.label === 'realtime' &&
          (isColumn ? (
            <div>
              <Typography variant="body2" component="div" style={{marginBottom: 8}}>
                Auto refresh interval
              </Typography>
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  value={realtimeRefreshIntervalSeconds}
                  onChange={event => {
                    const nextValue = Number(event.target.value);
                    setPersistentState('realtimeRefreshIntervalSeconds', nextValue);
                  }}
                  aria-label="Auto refresh interval"
                >
                  {REALTIME_REFRESH_INTERVAL_OPTIONS.map(({value, label}) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          ) : (
            <FormControl variant="outlined" size="small" style={{minWidth: 170}}>
              <InputLabel id="realtime-refresh-interval-label">Auto refresh interval</InputLabel>
              <Select
                labelId="realtime-refresh-interval-label"
                value={realtimeRefreshIntervalSeconds}
                label="Auto refresh interval"
                onChange={event => {
                  const nextValue = Number(event.target.value);
                  setPersistentState('realtimeRefreshIntervalSeconds', nextValue);
                }}
              >
                {REALTIME_REFRESH_INTERVAL_OPTIONS.map(({value, label}) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}

        {controlsConfig
          .filter(({prebidStackOnly, prebidOnly, analyticsExcluded, analyticsOnly, hidden}) => {
            if (prebidStackOnly && !isGroupingEnabled) return false;
            if (prebidOnly && type !== 'prebid') return false;
            if (analyticsExcluded && type === 'analytics') return false;
            if (analyticsOnly && type !== 'analytics') return false;
            if (hidden) return false;
            return true;
          })
          .map(({key, title, tooltip, checked, onChange, disabled}) => {
            const isChangeLogLoading = key === 'changeLog' && panelChangesLoading;

            return (
            <div key={title}>
              <FormControlLabel
                style={isColumn ? {marginLeft: 0, marginRight: 0} : undefined}
                control={
                  isChangeLogLoading ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width={42}
                      height={42}
                      flexShrink={0}
                    >
                      <CircularProgress size={24} color="primary" />
                    </Box>
                  ) : (
                    <Checkbox
                      checked={checked}
                      color="primary"
                      onChange={onChange}
                      disabled={disabled}
                    />
                  )
                }
                label={<Typography variant={'body2'}>{title}</Typography>}
              />
              {tooltip && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  component="div"
                  style={
                    isColumn
                      ? {marginLeft: 44, marginTop: -4, marginBottom: 4}
                      : {marginLeft: 44, marginTop: -8}
                  }
                >
                  {tooltip}
                </Typography>
              )}
              {key === 'comparison' && !isComparisonDisabled && (
                <div
                  style={{
                    marginLeft: 44,
                    marginTop: 4,
                    marginBottom: 0,
                    paddingRight: isColumn ? 0 : 8,
                  }}
                >
                  <ComparisonModeRadio
                    disabled={!comparisonEnabled || disabled}
                    showCurrentPeriodInDateContext={type === 'demand'}
                  />
                </div>
              )}
            </div>
            );
          })}

        {isColumn && (type === 'analytics' || type === 'demand') && (
          <Divider style={{marginTop: 4, marginBottom: 4}} />
        )}

        {(type === 'analytics' || type === 'demand') &&
          (isColumn ? (
            <div>
              <Typography variant="body2" component="div" style={{marginBottom: 8}}>
                Visible metrics
              </Typography>
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  value={maxVisibleColumns}
                  onChange={handleVisibleColumnsChange}
                  aria-label="Visible metrics"
                >
                  {[1, 2, 3, 4].map(val => (
                    <MenuItem key={val} value={val}>
                      {val}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          ) : (
            <FormControl variant="outlined" size="small" style={{minWidth: 150}}>
              <InputLabel id="visible-columns-label">Visible metrics</InputLabel>
              <Select
                labelId="visible-columns-label"
                value={maxVisibleColumns}
                onChange={handleVisibleColumnsChange}
                label="Visible metrics"
              >
                {[1, 2, 3, 4].map(val => (
                  <MenuItem key={val} value={val}>
                    {val}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}

        {isColumn && (
          <Divider
            style={{
              marginTop: type === 'analytics' || type === 'demand' ? 4 : 0,
              marginBottom: 4,
            }}
          />
        )}

        {isColumn && (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                marginBottom: 4,
              }}
            >
              <Typography variant="body2" component="div">
                Graph height
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={isGraphHeightAtDefault}
                onClick={() =>
                  setPersistentState('stackedReportGraphHeightPx', graphSettings.wrapperHeight)
                }
              >
                Reset
              </Button>
            </div>
            <Slider
              key={`graph-height-slider-${graphHeightSliderMin}-${graphHeightSliderMax}`}
              value={graphHeightSliderValue}
              min={graphHeightSliderMin}
              max={graphHeightSliderMax}
              step={STACKED_REPORT_GRAPH_HEIGHT.step}
              marks={[{value: graphSettings.wrapperHeight}]}
              classes={{
                mark: graphHeightSliderClasses.mark,
              }}
              onChange={(_, value) => setPersistentState('stackedReportGraphHeightPx', value)}
              valueLabelDisplay="off"
              color="primary"
              aria-label="Graph height in pixels"
            />
            <Typography
              variant="caption"
              color="textSecondary"
              component="div"
              style={{marginTop: 8}}
            >
              Sticky graph uses the same height ratio.
            </Typography>
          </div>
        )}
      </div>
    );
  }
);

export default GraphControls;
